// Icon set — thin wrapper around lucide-react.
//
// We keep our own IconName enum + <Icon name="..." /> API so the rest of
// the codebase doesn't have to import lucide-react directly. Adding a
// new icon means: pick the right Lucide name from https://lucide.dev,
// add it to ICONS below, and add the alias to IconName.
//
// Lucide icons are hand-drawn, professionally designed, and consistent
// with shadcn / Vercel / Tailwind UI. No more sketchy hand-rolled SVGs.

import {
  Menu,
  Map as LMap,
  PlaneTakeoff,
  FileText,
  Route as LRoute,
  Plane,
  Activity,
  Settings,
  MoreHorizontal,
  Layers,
  Crosshair,
  Flame,
  Search,
  Plus,
  Trash2,
  Pencil,
  Play,
  Square,
  Pause,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Gauge,
  Package,
  Wind,
  ClipboardList,
  Ruler,
  Navigation,
  BookText,
  FolderOpen,
  X,
  RadioTower,
  Users,
  ShieldCheck,
  Loader2,
  // Extended set — used throughout the app; centralised here so no file
  // needs to import lucide-react directly.
  Zap,
  WifiOff,
  MapPin,
  Clock,
  AlertCircle,
  RefreshCw,
  Sun,
  Sunset,
  Mountain,
  Droplet,
  AlertTriangle,
  Radio,
  Cloud,
  SatelliteDish,
  TowerControl,
  Headphones,
  Megaphone,
  Check,
  CheckCircle,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Minus,
  PlaneLanding,
  User,
  Building2,
  ExternalLink,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  Mail,
  Send,
  Bot,
  Phone,
  MessageSquare,
  MoveDown,
  SlidersVertical,
  Helicopter,
  Unlock,
  Repeat,
  Flag,
  XCircle,
  Volume2,
  VolumeX,
  BookOpen,
  HelpCircle,
  ChevronUp,
  BarChart2,
  ShieldAlert,
  ImageIcon,
  Circle,
  RotateCcw,
  RotateCw,
  Shield,
  Target,
  Car,
  GripVertical,
  KeyRound,
  UserX,
  Compass,
  Download,
  Printer,
  type LucideIcon,
} from 'lucide-react';

export type IconName =
  | 'menu'
  | 'map'
  | 'airport'
  | 'plate'
  | 'route'
  | 'plane'
  | 'track'
  | 'gear'
  | 'more'
  | 'layers'
  | 'crosshair'
  | 'fire'
  | 'search'
  | 'plus'
  | 'trash'
  | 'edit'
  | 'play'
  | 'stop'
  | 'pause'
  | 'chevron-right'
  | 'chevron-left'
  | 'chevron-down'
  | 'gauge'
  | 'pack'
  | 'wind'
  | 'flights'
  | 'ruler'
  | 'direct-to'
  | 'book'
  | 'documents'
  | 'x'
  | 'incidents'
  | 'crew'
  | 'frat'
  | 'loader';

// Custom SVG icons (drawn here when lucide doesn't ship something
// suitable). Each function returns a JSX element rendered at the
// className the caller passed in. Stroke width matches lucide's 1.8.
const CUSTOM_ICONS = {
  'crossed-runways': (className: string) => (
    // Two rotated rectangles sharing the (12, 12) center. Outlined
    // (fill='none' + 1.5 stroke) so they read as runways at small
    // sizes; perfectly rectangular ends because the strokes use
    // 'square' line caps and the rects are symmetric around the
    // rotation pivot. Center-line dashes inside each runway.
    // (Boss reported the previous version had crooked ends because
    // the rounded line-caps were bleeding past the rect edges and the
    // rects weren't centered on the rotation pivot. Fixed 2026-05-12.)
    <svg
      className={`fyrefly-icon ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="square"
      strokeLinejoin="miter"
      aria-hidden="true"
    >
      {/* Long runway: 22×3.4, centered at (12,12), rotated -40°.
          x = 12 - 22/2 = 1, y = 12 - 3.4/2 = 10.3 */}
      <rect
        x="1" y="10.3" width="22" height="3.4"
        transform="rotate(-40 12 12)"
      />
      <line
        x1="3.5" y1="12" x2="20.5" y2="12"
        strokeWidth={1}
        strokeDasharray="1.3 1.7"
        transform="rotate(-40 12 12)"
      />
      {/* Short runway: 16×2.8, centered at (12,12), rotated +50°.
          x = 12 - 16/2 = 4, y = 12 - 2.8/2 = 10.6 */}
      <rect
        x="4" y="10.6" width="16" height="2.8"
        transform="rotate(50 12 12)"
      />
      <line
        x1="5.5" y1="12" x2="18.5" y2="12"
        strokeWidth={1}
        strokeDasharray="1.1 1.5"
        transform="rotate(50 12 12)"
      />
    </svg>
  ),
} as const;

type CustomIconKey = keyof typeof CUSTOM_ICONS;

const ICONS: Record<IconName, LucideIcon | CustomIconKey> = {
  // Navigation & shell
  menu: Menu,
  map: LMap,
  // Airport: two crossed runways (per boss request 2026-05-12) — lucide
  // doesn't ship a runway icon so we render an inline SVG. See `Icon`
  // below for the custom-SVG path.
  airport: 'crossed-runways',
  plate: FileText,          // approach plates = documents
  route: LRoute,            // a real signposted route icon
  plane: Plane,             // aircraft profiles ("my fleet")
  // Flights dashboard now uses the plane-taking-off silhouette since
  // the runways now live on the Airports icon. Matches how ForeFlight
  // organizes the same two surfaces.
  flights: PlaneTakeoff,
  track: Activity,          // GPS breadcrumb = heartbeat line
  gear: Settings,           // (we keep the 'gear' alias to avoid touching consumers)
  more: MoreHorizontal,

  // Map controls
  layers: Layers,
  crosshair: Crosshair,
  fire: Flame,
  search: Search,
  gauge: Gauge,
  pack: Package,
  wind: Wind,
  ruler: Ruler,
  'direct-to': Navigation,

  // Verbs
  plus: Plus,
  trash: Trash2,
  edit: Pencil,
  play: Play,
  stop: Square,
  pause: Pause,

  // More-tab destinations
  book: BookText,           // logbook (career totals + flight log)
  documents: FolderOpen,    // documents library (private uploads + public shelf)

  // Fire ops
  incidents: RadioTower,    // incident management
  crew: Users,              // crew roster
  frat: ShieldCheck,        // flight risk assessment (pre-flight safety check)

  // Disclosure
  'chevron-right': ChevronRight,
  'chevron-left': ChevronLeft,
  'chevron-down': ChevronDown,

  // Close
  x: X,

  // Loading
  loader: Loader2,
};

// ── Named re-exports ────────────────────────────────────────────────────────
// Prefer `import { SomeIcon } from '../shell/Icon'` over importing lucide-react
// directly. All icons the app uses should flow through this file so we have
// one canonical inventory and bundle analysis surface.
export {
  // Core / shell (already mapped in ICONS above)
  Menu,
  LMap as Map,
  PlaneTakeoff,
  FileText,
  Plane,
  Activity,
  Settings,
  MoreHorizontal,
  Layers,
  Crosshair,
  Flame,
  Search,
  Plus,
  Trash2,
  Pencil,
  Play,
  Square,
  Pause,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Gauge,
  Package,
  Wind,
  ClipboardList,
  Ruler,
  Navigation,
  BookText,
  FolderOpen,
  X,
  RadioTower,
  Users,
  ShieldCheck,
  Loader2,
  // Extended set
  Zap,
  WifiOff,
  MapPin,
  Clock,
  AlertCircle,
  RefreshCw,
  Sun,
  Sunset,
  Mountain,
  Droplet,
  AlertTriangle,
  Radio,
  Cloud,
  SatelliteDish,
  TowerControl,
  Headphones,
  Megaphone,
  Check,
  CheckCircle,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Minus,
  PlaneLanding,
  User,
  Building2,
  ExternalLink,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  Mail,
  Send,
  Bot,
  Phone,
  MessageSquare,
  MoveDown,
  SlidersVertical,
  Helicopter,
  Unlock,
  Repeat,
  Flag,
  XCircle,
  Volume2,
  VolumeX,
  BookOpen,
  HelpCircle,
  ChevronUp,
  BarChart2,
  ShieldAlert,
  ImageIcon,
  Circle,
  RotateCcw,
  RotateCw,
  Shield,
  Target,
  Car,
  GripVertical,
  KeyRound,
  UserX,
  Compass,
  Download,
  Printer,
  type LucideIcon,
};

export function Icon({ name, className = 'h-5 w-5' }: { name: IconName; className?: string }) {
  const entry = ICONS[name];
  if (typeof entry === 'string') {
    return CUSTOM_ICONS[entry](className);
  }
  // JSX needs an uppercase identifier to be treated as a component.
  const Cmp = entry;
  // Lucide accepts standard SVG attributes; default stroke width matches our
  // previous hand-rolled icons closely enough that the visual rhythm of the
  // UI doesn't shift.
  // Append a marker class so our currentColor / backdrop-filter
  // workaround in styles.css can target every icon — lucide and
  // custom-SVG — with the same rule. iOS Safari otherwise drops
  // the stroke inside backdrop-blur ancestors.
  return <Cmp className={`fyrefly-icon ${className}`} strokeWidth={1.8} aria-hidden="true" />;
}
